import {
	Component,
	ElementRef,
	HostListener,
	Inject,
	OnDestroy,
	OnInit,
	Renderer2,
	DOCUMENT,
	ChangeDetectionStrategy,
} from '@angular/core';

import { Title } from '@angular/platform-browser';

import { first, lastValueFrom, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as Waves from 'node-waves';

import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from '@core/services/config.service';
import { CoreLoadingScreenService } from '@core/services/loading-screen.service';

import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { AuthenticationService } from './server/services';
import { CoreMenu } from '@core/types';
import { menu } from './menu/menu';
import { universityMenu } from './menu/university';
import { schoolMenu } from './menu/school';
import { frontendMenu } from './menu/frontend';
import { internshipSecretaryMenu } from './menu/internship-secretary';
import { tutorMenu } from './menu/tutor';
import { TranslateService } from '@ngx-translate/core';
import { LayoutType } from './server/enums/layout-type';
import { CurrentUniversityService } from './server/services/current-university.service';
import { CurrentSchoolUniversityService } from './server/services/current-school-university.service';
import { RoleType } from './server/enums/role-type';
import { CareerService } from './server/services/career.service';
import { CurrentCareerService } from './server/services/current-career.service';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
	// Controllo misura minima richiesta per l'utilizzo della piattaforma
	@HostListener('window:resize', ['$event'])
	onResize(event) {
		this.innerWidth = window.innerWidth;
	}

	innerWidth: number = window.innerWidth;
	coreConfig: any;
	menu: Array<CoreMenu> = [];
	universityMenu: Array<CoreMenu> = [];
	schoolMenu: Array<CoreMenu> = [];
	frontendMenu: Array<CoreMenu> = [];
	internshipSecretaryMenu: Array<CoreMenu> = [];
	tutorMenu: Array<CoreMenu> = [];
	layout: 'backend' | 'frontend' = 'backend';
	allowSmallScreens: boolean = false;
	// public returnUrl: string;

	// Private
	private _unsubscribeAll: Subject<any>;

	constructor(
		@Inject(DOCUMENT) private document: any,
		private _title: Title,
		private _renderer: Renderer2,
		private _elementRef: ElementRef,
		public _coreConfigService: CoreConfigService,
		private _coreSidebarService: CoreSidebarService,
		private _coreLoadingScreenService: CoreLoadingScreenService,
		private _coreMenuService: CoreMenuService,
		private _router: Router,
		private _authenticationService: AuthenticationService,
		private _translateService: TranslateService,
		private _currentUniversityService: CurrentUniversityService,
		private _currentSchoolUniversityService: CurrentSchoolUniversityService,
		private _careerService: CareerService,
		private _currentCareerService: CurrentCareerService
	) {
		// Set the private defaults
		this._unsubscribeAll = new Subject();

		this.schoolMenu = JSON.parse(JSON.stringify(schoolMenu));
		this.universityMenu = JSON.parse(JSON.stringify(universityMenu));
		this.frontendMenu = JSON.parse(JSON.stringify(frontendMenu));
		this.internshipSecretaryMenu = JSON.parse(JSON.stringify(internshipSecretaryMenu));
		this.tutorMenu = JSON.parse(JSON.stringify(tutorMenu));
		this.menu = JSON.parse(JSON.stringify(menu));

		this._authenticationService.currentActiveProfile.pipe(takeUntil(this._unsubscribeAll)).subscribe(async activeProfile => {
			if (activeProfile && !this._authenticationService.refreshingToken) {
				this.allowSmallScreens =
					activeProfile.profile_id === RoleType.Student ||
					activeProfile.profile_id === RoleType.InternshipTutor ||
					activeProfile.profile_id === RoleType.InternshipCoordinatorProfessor;

				if (activeProfile.profile.layout_type_id === LayoutType.FrontOffice) {
					this.layout = 'frontend';

					if (activeProfile.profile_id === RoleType.Trainee) {
						if (!this._currentCareerService.currentCareerId.value) {
							this._authenticationService.Loginloading = true;
							const res = await lastValueFrom(this._careerService.getCareersFilteredByAuthUser());
							this._authenticationService.Loginloading = false;
							if (res.data.length === 1) {
								this._currentCareerService.changeActiveCareer(res.data[0]);
							} else {
								this._careerService.careersFilteredBySchoolAndAuthUser.next(res.data);
							}
						}

						// Subscribe to career changes to dynamically hide/show "Nuova Registrazione" menu item
						// Using filter to ensure career is loaded before processing menu
						this._currentCareerService.currentCareer
							.pipe(
								filter(career => !!career),
								takeUntil(this._unsubscribeAll)
							)
							.subscribe(career => {
								// Create fresh copy of menu from original
								let menuForTrainee = JSON.parse(JSON.stringify(frontendMenu));
								menuForTrainee = this._authenticationService.filterCombinedItems(menuForTrainee, activeProfile);

								// Filter out "registration" item if logbook is closed or if closing date is not null
								if (this._currentCareerService.isCareerClosed(career)) {
									menuForTrainee = menuForTrainee.filter(item => item.id !== 'registration');
								}

								if (this._coreMenuService.menuKeyExist('frontend')) {
									this._coreMenuService.unregister('frontend');
								}

								this._coreMenuService.register('frontend', menuForTrainee);
								this._coreMenuService.setCurrentMenu('frontend');
							});
					} else if (
						activeProfile.profile_id === RoleType.InternshipTutor ||
						activeProfile.profile_id === RoleType.InternshipCoordinatorProfessor ||
						activeProfile.profile_id === RoleType.InternshipSecretary
					) {
						this.layout = 'frontend';
						this._coreConfigService.setConfig({
							layout: {
								type: 'horizontal',
								skin: 'bordered',
								menu: { hidden: false },
								footer: { hidden: false },
								navbar: { hidden: false },
							},
						});
						this.tutorMenu = this._authenticationService.filterCombinedItems(this.tutorMenu, activeProfile);
						this._coreMenuService.register('tutor', this.tutorMenu);
						this._coreMenuService.setCurrentMenu('tutor');
					} else {
						// For non-trainee roles, use the base menu without career filtering
						let baseFrontendMenu = JSON.parse(JSON.stringify(frontendMenu));
						baseFrontendMenu = this._authenticationService.filterCombinedItems(baseFrontendMenu, activeProfile);
						this._coreMenuService.register('frontend', baseFrontendMenu);
						this._coreMenuService.setCurrentMenu('frontend');
					}

					if (
						(activeProfile.profile_id === RoleType.Trainee && this._currentCareerService.currentCareerId.value) ||
						activeProfile.profile_id !== RoleType.Trainee
					) {
						if (this._router.url.startsWith('/authentication/login')) {
							this._coreConfigService.setConfig({
								layout: {
									type: 'horizontal',
									skin: 'bordered',
									menu: { hidden: false },
									footer: { hidden: false },
									navbar: { hidden: false },
								},
							});
							if (
								activeProfile.profile_id === RoleType.InternshipTutor ||
								activeProfile.profile_id === RoleType.InternshipCoordinatorProfessor
							) {
								this._router.navigate(['/tutor']);
							} else if (activeProfile.profile_id === RoleType.InternshipSecretary) {
								this._router.navigate(['/internship-secretary']);
							} else if (activeProfile.profile_id === RoleType.Student) {
								this._router.navigate(['/student']);
							} else if (activeProfile.profile_id === RoleType.Trainee) {
								this._router.navigate(['/dashboard']);
							} else {
								this._router.navigate(['/dashboard']);
							}
						}
					}
				} else if (activeProfile.profile.layout_type_id === LayoutType.BackOffice) {
					if (
						activeProfile.profile_id === RoleType.InternshipTutor ||
						activeProfile.profile_id === RoleType.InternshipCoordinatorProfessor ||
						activeProfile.profile_id === RoleType.InternshipSecretary
					) {
						this.layout = 'frontend';
						this._coreConfigService.setConfig({
							layout: {
								type: 'horizontal',
								skin: 'bordered',
								menu: { hidden: false },
								footer: { hidden: false },
								navbar: { hidden: false },
							},
						});

						if (activeProfile.profile_id === RoleType.InternshipSecretary) {
							this.internshipSecretaryMenu = this._authenticationService.filterCombinedItems(this.internshipSecretaryMenu, activeProfile);
							this._coreMenuService.register('university', this.internshipSecretaryMenu);
							this._coreMenuService.setCurrentMenu('university');
						} else {
							this.tutorMenu = this._authenticationService.filterCombinedItems(this.tutorMenu, activeProfile);
							this._coreMenuService.register('tutor', this.tutorMenu);
							this._coreMenuService.setCurrentMenu('tutor');
						}

						if (this._router.url.startsWith('/authentication/login')) {
							if (activeProfile.profile_id === RoleType.InternshipSecretary) {
								this._router.navigate(['/internship-secretary']);
							} else {
								this._router.navigate(['/tutor']);
							}
						}
					} else {
						this.layout = 'backend';

						this._coreConfigService.setConfig({
							layout: {
								type: activeProfile.profile_id === RoleType.InternshipSecretary ? 'horizontal' : 'vertical',
								skin: 'bordered',
								menu: { hidden: false },
								footer: { hidden: false },
								navbar: { hidden: false },
							},
						});

						if (this._currentSchoolUniversityService.currentSchoolId.value && this._currentUniversityService.currentUniversityId.value) {
							this.schoolMenu = this._authenticationService.filterCombinedItems(this.schoolMenu, activeProfile);
							this._coreMenuService.register('school', this.schoolMenu);
							this._coreMenuService.setCurrentMenu('school');

							if (this._router.url.startsWith('/authentication/login')) {
								this._router.navigate(['/university/school/home']);
							}
						} else if (this._currentUniversityService.currentUniversityId.value) {
							if (activeProfile.profile_id === RoleType.InternshipSecretary) {
								this.internshipSecretaryMenu = this._authenticationService.filterCombinedItems(this.internshipSecretaryMenu, activeProfile);
								this._coreMenuService.register('university', this.internshipSecretaryMenu);
							} else {
								this.universityMenu = this._authenticationService.filterCombinedItems(this.universityMenu, activeProfile);
								this._coreMenuService.register('university', this.universityMenu);
							}

							this._coreMenuService.setCurrentMenu('university');

							if (this._router.url.startsWith('/authentication/login')) {
								if (activeProfile.profile_id === RoleType.InternshipSecretary) {
									this._router.navigate(['/internship-secretary']);
								} else {
									this._router.navigate(['/university/home']);
								}
							}

							this.universityMenu = this._authenticationService.filterCombinedItems(this.universityMenu, activeProfile);
							this._coreMenuService.register('university', this.universityMenu);
							this._coreMenuService.setCurrentMenu('university');
						} else {
							if (activeProfile.profile_id === RoleType.InternshipSecretary) {
								this._coreConfigService.setConfig({
									layout: {
										type: 'horizontal',
										menu: { hidden: false },
										footer: { hidden: false },
										navbar: { hidden: false },
									},
								});
								this.internshipSecretaryMenu = this._authenticationService.filterCombinedItems(this.internshipSecretaryMenu, activeProfile);
								this._coreMenuService.register('internship-secretary', this.internshipSecretaryMenu);
								this._coreMenuService.setCurrentMenu('internship-secretary');

								if (this._router.url.startsWith('/authentication/login')) {
									this._router.navigate(['/internship-secretary']);
								}
							} else {
								this.menu = this._authenticationService.filterCombinedItems(this.menu, activeProfile);
								this._coreMenuService.register('main', this.menu);
								this._coreMenuService.setCurrentMenu('main');

								if (this._router.url.startsWith('/authentication/login')) {
									this._router.navigate(['/home']);
								}
							}
						}
					}
				} else if (!this._router.url.startsWith('/authentication/login')) {
					this._authenticationService.logout();
				}
			}
		});

		this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
			this.coreConfig = config;

			// Remove default classes first
			this._elementRef.nativeElement.classList.remove('vertical-layout', 'vertical-menu-modern', 'horizontal-layout', 'horizontal-menu');
			// Add class based on config options
			if (this.coreConfig.layout.type === 'vertical') {
				this._elementRef.nativeElement.classList.add('vertical-layout', 'vertical-menu-modern');
			} else if (this.coreConfig.layout.type === 'horizontal') {
				this._elementRef.nativeElement.classList.add('horizontal-layout', 'horizontal-menu');
			}

			// Navbar
			//--------

			// Remove default classes first
			this._elementRef.nativeElement.classList.remove('navbar-floating', 'navbar-static', 'navbar-sticky', 'navbar-hidden');

			// Add class based on config options
			if (this.coreConfig.layout.navbar.type === 'navbar-static-top') {
				this._elementRef.nativeElement.classList.add('navbar-static');
			} else if (this.coreConfig.layout.navbar.type === 'fixed-top') {
				this._elementRef.nativeElement.classList.add('navbar-sticky');
			} else if (this.coreConfig.layout.navbar.type === 'floating-nav') {
				this._elementRef.nativeElement.classList.add('navbar-floating');
			} else {
				this._elementRef.nativeElement.classList.add('navbar-hidden');
			}

			// Footer
			//--------

			// Remove default classes first
			this._elementRef.nativeElement.classList.remove('footer-fixed', 'footer-static', 'footer-hidden');

			// Add class based on config options
			if (this.coreConfig.layout.footer.type === 'footer-sticky') {
				this._elementRef.nativeElement.classList.add('footer-fixed');
			} else if (this.coreConfig.layout.footer.type === 'footer-static') {
				this._elementRef.nativeElement.classList.add('footer-static');
			} else {
				this._elementRef.nativeElement.classList.add('footer-hidden');
			}

			// Blank layout
			if (this.coreConfig.layout.menu.hidden && this.coreConfig.layout.navbar.hidden && this.coreConfig.layout.footer.hidden) {
				this._elementRef.nativeElement.classList.add('blank-page');
				// ! Fix: Transition issue while coming from blank page
				//this._renderer.setAttribute(this._elementRef.nativeElement.getElementsByClassName('app-content')[0], 'style', 'transition:none');
			} else {
				this._elementRef.nativeElement.classList.remove('blank-page');
				// ! Fix: Transition issue while coming from blank page
				setTimeout(() => {
					this._renderer.setAttribute(
						this._elementRef.nativeElement.getElementsByClassName('app-content')[0],
						'style',
						'transition:300ms ease all'
					);
				}, 0);
				// If navbar hidden
				if (this.coreConfig.layout.navbar.hidden) {
					this._elementRef.nativeElement.classList.add('navbar-hidden');
				}
				// Menu (Vertical menu hidden)
				if (this.coreConfig.layout.menu.hidden) {
					this._renderer.setAttribute(this._elementRef.nativeElement, 'data-col', '1-column');
				} else {
					this._renderer.removeAttribute(this._elementRef.nativeElement, 'data-col');
				}
				// Footer
				if (this.coreConfig.layout.footer.hidden) {
					this._elementRef.nativeElement.classList.add('footer-hidden');
				}
			}

			// Skin Class (Adding to body as it requires highest priority)
			if (this.coreConfig.layout.skin !== '' && this.coreConfig.layout.skin !== undefined) {
				this.document.body.classList.remove('default-layout', 'bordered-layout', 'dark-layout', 'semi-dark-layout');
				this.document.body.classList.add(this.coreConfig.layout.skin + '-layout');
			}
		});
	}

	// Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */
	ngOnInit(): void {
		// Init wave effect (Ripple effect)
		Waves.init();
		// Subscribe to config changes

		this._router.events.pipe(takeUntil(this._unsubscribeAll)).subscribe(val => {
			if (val instanceof NavigationStart && val.id > 1) {
				this._coreLoadingScreenService.show();
			}
			if (val instanceof NavigationStart) {
				// Uscita dal contesto di scuola/ateneo quando si lascia il suo albero di rotte (logo, link, menu, indietro).
				// Fino al 03/11/2026 (#19776) questa logica stava in un handler di popstate: si attivava solo dopo un
				// "indietro" del browser e aggiungeva una sottoscrizione a ogni popstate, con la rotta di partenza vecchia;
				// col logo si finiva su "Pagina non autorizzata" (MultitenantGuard) e le prime chiamate del nuovo livello
				// partivano ancora con gli header del livello lasciato ("Permesso negato").
				const currentRoute = this._router.url;
				const destinationRoute = val.url;
				if (currentRoute.includes('university/school') && !destinationRoute.includes('university/school')) {
					this._currentSchoolUniversityService.exitSchool();
				}
				if (currentRoute.includes('/university') && !destinationRoute.includes('/university')) {
					this._currentUniversityService.exitUniversity();
				}
				if (destinationRoute.includes('/authentication/login')) {
					this._coreConfigService.isConfigured = false;
				}
			}
			if (val instanceof NavigationEnd) {
				// Refresh career data when navigating to ensure latest career status
				if (this._authenticationService.activeProfileId.value === RoleType.Trainee && this._currentCareerService.currentCareerId.value) {
					this._careerService.refreshCurrentCareer();
				}

				if (this._authenticationService.activeProfileId.value === RoleType.Trainee && val.url !== '/authentication/login') {
					this._currentCareerService.currentCareer
						.pipe(
							filter(currentCareer => !!currentCareer),
							first()
						)
						.subscribe(activeCareer => {
							setTimeout(() => {
								this._coreLoadingScreenService.hide();
							}, 100);
						});
				} else {
					setTimeout(() => {
						this._coreLoadingScreenService.hide();
					}, 100);
				}
			}

			// Set loading state to false in both of the below events to hide the spinner in case a request fails
			if (val instanceof NavigationCancel) {
				setTimeout(() => {
					this._coreLoadingScreenService.hide();
				}, 100);
			}
			if (val instanceof NavigationError) {
				setTimeout(() => {
					this._coreLoadingScreenService.hide();
				}, 100);
			}
		});

		// Set the application page title
		this._title.setTitle(this.coreConfig?.app?.appTitle);
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		// Unsubscribe from all subscriptions
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}

	// Public methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Toggle sidebar open
	 *
	 * @param key
	 */
	toggleSidebar(key): void {
		this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
	}
}
